import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { Router } from '@angular/router';
import {
  FuseMediaWatcherService,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fleet/fuse';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FleetNavigationService } from '@fleet/navigation';

import { FuseNavigationItem } from '@fleet/fuse/lib/components';
import { FlexCommunicatorService } from '@fleet/flex/flex-communicator.service';
import { GroupHierarchyItem } from '@fleet/model';
import { ProductConfigurationService } from '@fleet/product-configuration';

@Component({
    selector: 'fleet-flex-layout',
    templateUrl: './fleet-flex-layout.component.html',
    styleUrls: ['./fleet-flex-layout.component.scss'],
    standalone: false
})
export class FleetFlexLayoutComponent implements OnInit, OnDestroy {
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  fleetProduct: string;
  isScreenSmall: boolean;
  navigation: FuseNavigationItem[];
  currentGroup: GroupHierarchyItem;
  logoSrc: string = 'assets/logos/fleet-driver-horiz.svg';
  envName: string;
  /**
   * Constructor
   */
  constructor(
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private router: Router,
    private _fuseNavigationService: FuseNavigationService,
    private fleetNavigationService: FleetNavigationService,
    private changeDetectorRef: ChangeDetectorRef,
    private _productConfigurationService: ProductConfigurationService,
    private flexCommunicatorService: FlexCommunicatorService,
    @Inject('env') env: any
  ) {
    this.fleetProduct = env.fleetProduct;
    this.envName = env.envName;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('sm');
      });

    this.fleetNavigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (navigation: FuseNavigationItem[]) => {
          this.navigation = navigation;
          this.changeDetectorRef.markForCheck();
        },
      });

    this.fleetNavigationService.currentGroup$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (currentGroup: GroupHierarchyItem) => {
          this.currentGroup = currentGroup;
        },
      });

    this._productConfigurationService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (config: any) => {
          if (config.horizontalLogo) {
            this.logoSrc = config.horizontalLogo;
            this.changeDetectorRef.markForCheck();
          }
        },
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    // Clean up flex communicator service
    this.flexCommunicatorService.destroy();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  onNewJobClick(): void {
    const queryParams: any = {};

    // Get all route parameters from the route tree using router state
    const getAllRouteParams = (): any => {
      let params = {};

      // Start from root and traverse all routes
      let route = this.router.routerState.root;
      while (route) {
        params = { ...params, ...route.snapshot.params };
        route = route.firstChild;
      }

      return params;
    };

    const allParams = getAllRouteParams();

    // Check for traveller ID
    if (allParams['travellerId']) {
      queryParams.travellerId = allParams['travellerId'];
    }

    // Check for organisation ID
    if (allParams['organisationId']) {
      queryParams.organisationId = allParams['organisationId'];
    }

    // Navigate to job/new with query params if any were found
    console.log('routing new job with queryParams', queryParams);
    this.router.navigate(['/job/new'], { queryParams });
    //
  }
}
